import { useState } from "react";
import { createPortal } from "react-dom";
import "./ExtraChargesCard.css";

const TYPE_LABELS = {
  deliveryFee: "Delivery Fee",
  serviceCharge: "Service Charge",
  extraPayment: "Off-record Debt",
  other: "",
};

const TYPE_STYLES = {
  deliveryFee: { bg: "#eaf2fd", border: "#c5dcf7", color: "#1e3a5f" },
  serviceCharge: { bg: "#e8f7ee", border: "#b2e2c5", color: "#2a7a4e" },
  extraPayment: { bg: "#fff3e8", border: "#f5ceaa", color: "#b85c00" },
  other: { bg: "#f5f0ff", border: "#d8c5f7", color: "#5b3fa0" },
};

function ExtraChargeModal({ onClose, onAdd, currency }) {
  const [chargeType, setChargeType] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [amount, setAmount] = useState("");

  const isOther = chargeType === "other";
  const labelOk = !isOther || customLabel.trim().length > 0;
  const canSubmit = chargeType && parseFloat(amount) > 0 && labelOk;

  const handleTypeChange = (e) => {
    setChargeType(e.target.value);
    setCustomLabel("");
  };

  const handleAdd = () => {
    if (!canSubmit) return;
    const label = isOther ? customLabel.trim() : TYPE_LABELS[chargeType];
    onAdd({ type: chargeType, label, amount: parseFloat(amount), id: Date.now() });
    onClose();
  };

  return createPortal(
    <div className='aec-overlay' onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='aec-modal'>
        {/* Header */}
        <div className='aec-modal-header'>
          <div className='aec-modal-icon'>
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <rect x='2' y='5' width='20' height='14' rx='2' />
              <line x1='2' y1='10' x2='22' y2='10' />
            </svg>
          </div>
          <div>
            <div className='aec-modal-title'>Add Extra Charge</div>
            <div className='aec-modal-subtitle'>Select charge type and enter amount</div>
          </div>
          <button className='aec-modal-close' onClick={onClose} aria-label='Close'>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className='aec-modal-body'>
          {/* Charge type select */}
          <div className='aec-field'>
            <label>Charge Type</label>
            <div className='aec-select-wrap'>
              <select value={chargeType} onChange={handleTypeChange}>
                <option value=''>— Select a charge type —</option>
                <option value='deliveryFee'>Delivery Fee</option>
                <option value='serviceCharge'>Service Charge</option>
                <option value='extraPayment'>Off-record Debt / Balance</option>
              </select>
              <span className='aec-chevron'>
                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                  <polyline points='6 9 12 15 18 9' />
                </svg>
              </span>
            </div>
          </div>

          {/* Custom label — only for "Other" */}
          {isOther && (
            <div className='aec-field'>
              <label>Custom Label</label>
              <input
                type='text'
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder='e.g. Setup fee, Handling charge…'
                maxLength={60}
                autoFocus
              />
            </div>
          )}

          <hr className='aec-divider' />

          {/* Amount */}
          <div className='aec-field'>
            <label>Amount</label>
            <div className='aec-amount-wrap'>
              <span className='aec-currency'>{currency}</span>
              <input type='number' value={amount} onChange={(e) => setAmount(e.target.value)} placeholder='0.00' min='0' step='0.01' />
            </div>
          </div>

          {/* Note */}
          {/*  <div className='aec-field'>
            <label>
              Note <span className='aec-label-optional'>(optional)</span>
            </label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder='Add a note for this charge…' />
          </div> */}
        </div>

        {/* Footer */}
        <div className='aec-modal-footer'>
          <button className='aec-btn-cancel' onClick={onClose}>
            Cancel
          </button>
          <button className='aec-btn-add' onClick={handleAdd} disabled={!canSubmit}>
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
              <line x1='12' y1='5' x2='12' y2='19' />
              <line x1='5' y1='12' x2='19' y2='12' />
            </svg>
            Add Charge
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ExtraChargesCard({ charges = [], onChange, currency }) {
  const [modalOpen, setModalOpen] = useState(false);

  const handleAdd = (charge) => {
    console.log("Adding charge:", charge);

    //check if charge with same type and label already exists

    if (charges.some((c) => c.type === charge.type && c.label === charge.label)) {
      alert("A charge with the same type and label already exists. Please edit the existing charge or choose a different label.");
      return;
    }

    onChange([...charges, charge]);
  };

  const handleRemove = (id) => {
    onChange(charges.filter((c) => c.id !== id));
  };

  const total = charges.reduce((s, c) => s + c.amount, 0);
  const sublabel =
    charges.length > 0
      ? `${charges.length} charge${charges.length > 1 ? "s" : ""} added · ${currency}${total.toLocaleString("en", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })} total`
      : "Add delivery fee, service charge, or off-record balance";

  return (
    <div className='aec-wrap'>
      {/* Trigger card */}
      <div className='aec-trigger-card' onClick={() => setModalOpen(true)}>
        <div className='aec-trigger-left'>
          <div className='aec-icon-wrap'>
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <circle cx='12' cy='12' r='10' />
              <line x1='12' y1='8' x2='12' y2='16' />
              <line x1='8' y1='12' x2='16' y2='12' />
            </svg>
          </div>
          <div>
            <div className='aec-label'>Extra Payments</div>
            <div className='aec-sublabel'>{sublabel}</div>
          </div>
        </div>
        <button
          className='aec-add-btn'
          onClick={(e) => {
            e.stopPropagation();
            setModalOpen(true);
          }}
        >
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3'>
            <line x1='12' y1='5' x2='12' y2='19' />
            <line x1='5' y1='12' x2='19' y2='12' />
          </svg>
          Add Charge
        </button>
      </div>

      {/* Badges */}
      {charges.length > 0 && (
        <div className='aec-badges'>
          {charges.map((c) => {
            const s = TYPE_STYLES[c.type] || TYPE_STYLES.other;
            return (
              <div key={c.id} className='aec-badge' style={{ background: s.bg, borderColor: s.border, color: s.color }}>
                <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' style={{ flexShrink: 0 }}>
                  <line x1='12' y1='1' x2='12' y2='23' />
                  <path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' />
                </svg>
                <span>
                  {c.label}: {currency}
                  {c.amount.toLocaleString("en", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </span>
                <button className='aec-badge-remove' onClick={() => handleRemove(c.id)} title='Remove'>
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modalOpen && <ExtraChargeModal onClose={() => setModalOpen(false)} onAdd={handleAdd} currency={currency} />}
    </div>
  );
}
